import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { Button, Flex, Popover, Typography, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  Eyebrow,
  PillToggleGroup,
  ROUGH_NOTATION_BRACKETS,
  STANDOUT_MARKERS,
  type StandoutNotation,
} from "@libertify/ui";
import {
  KEY,
  PADDING_PRESETS,
  STYLE_COLORS,
  THICKNESS_PRESETS,
} from "./constants";
import { isStrokedNotation, findPresetColor } from "./helpers";
import { PillSegmented } from "../PillSegmented";
import type { RoughNotation, StandoutStyle } from "./types";

export type StandoutStylePopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notation: StandoutNotation;
  /** The mark as drawn now, to show which presets are on. */
  mark: RoughNotation;
  /** The color Default sets. */
  defaultColor: string;
  /** Each pick applies at once; the popover stays open for the next. */
  onChange: (patch: StandoutStyle) => void;
  /** The button it opens from and closes on. */
  children: ReactNode;
};

const BRACKET_PALETTE_SIDES =
  STANDOUT_MARKERS.find((m) => m.notation === "bracket")?.brackets ?? [];

/** Preset-only color, thickness, padding and bracket sides for one standout. */
export const StandoutStylePopover = ({
  open,
  onOpenChange,
  notation,
  mark,
  defaultColor,
  onChange,
  children,
}: StandoutStylePopoverProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  // From the drawn color, so a preset saved earlier shows as picked after a reload.
  const picked = findPresetColor(mark.color)?.value;
  // No sides saved → the viewer draws the palette's, so show those as picked.
  const drawnBrackets =
    mark.brackets.length > 0 ? mark.brackets : BRACKET_PALETTE_SIDES;
  const panel = useRef<HTMLDivElement>(null);
  // Not document.activeElement: Safari and macOS Firefox don't focus a clicked button.
  const trigger = useRef<HTMLSpanElement>(null);

  // The popover renders at the end of the page: bring focus in, on the current pick.
  const focusPanel = () => {
    const target =
      panel.current?.querySelector<HTMLElement>('[aria-pressed="true"]') ??
      panel.current?.querySelector<HTMLElement>("button");
    target?.focus({ preventScroll: true });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onOpenChange(false);
    trigger.current?.querySelector<HTMLElement>("button")?.focus();
  };

  // A slashed empty circle: Default keeps the color the standout loaded with.
  const defaultSwatch = `linear-gradient(135deg, transparent 45%, ${token.colorTextTertiary} 45% 55%, transparent 55%)`;
  const swatchStyle = (background: string, selected: boolean) => ({
    background,
    boxShadow: selected
      ? `0 0 0 2px ${token.colorBgElevated}, 0 0 0 4px ${token.colorText}`
      : undefined,
  });

  const heading = (text: string) => (
    <Eyebrow size="s" tone="default">
      {text}
    </Eyebrow>
  );

  const content = () => (
    <Flex
      ref={panel}
      vertical
      gap={10}
      style={{ width: 260 }}
      onKeyDown={onKeyDown}
    >
      {heading(t(`${KEY}.style.color`))}
      <Flex gap={8} wrap role="group" aria-label={t(`${KEY}.style.color`)}>
        <Button
          shape="circle"
          size="small"
          aria-label={t(`${KEY}.style.colors.default`)}
          aria-pressed={picked === undefined}
          style={swatchStyle(defaultSwatch, picked === undefined)}
          onClick={() => onChange({ color: defaultColor })}
        />
        {STYLE_COLORS.map(({ name, value }) => (
          <Button
            key={name}
            shape="circle"
            size="small"
            aria-label={t(`${KEY}.style.colors.${name}`)}
            aria-pressed={picked === value}
            style={swatchStyle(value, picked === value)}
            onClick={() => onChange({ color: value })}
          />
        ))}
      </Flex>
      {isStrokedNotation(notation) ? (
        <>
          {heading(t(`${KEY}.style.thickness`))}
          <PillSegmented
            $token={token}
            $wrap
            aria-label={t(`${KEY}.style.thickness`)}
            value={mark.stroke_width}
            onChange={(value) => onChange({ stroke_width: Number(value) })}
            options={THICKNESS_PRESETS.map(({ name, value }) => ({
              value,
              label: t(`${KEY}.style.thicknesses.${name}`),
            }))}
          />
          {heading(t(`${KEY}.style.padding`))}
          <PillSegmented
            $token={token}
            $wrap
            aria-label={t(`${KEY}.style.padding`)}
            value={mark.padding}
            onChange={(value) => onChange({ padding: Number(value) })}
            options={PADDING_PRESETS[notation].map(({ name, value }) => ({
              value,
              label: t(`${KEY}.style.paddings.${name}`),
            }))}
          />
          {notation === "bracket" ? (
            <>
              {heading(t(`${KEY}.style.sides`))}
              <PillToggleGroup
                required
                wrap
                aria-label={t(`${KEY}.style.sides`)}
                value={ROUGH_NOTATION_BRACKETS.filter((side) =>
                  drawnBrackets.includes(side)
                )}
                onChange={(brackets) => onChange({ brackets })}
                options={ROUGH_NOTATION_BRACKETS.map((side) => ({
                  value: side,
                  label: t(`${KEY}.style.sidesOptions.${side}`),
                }))}
              />
            </>
          ) : null}
        </>
      ) : (
        <Typography.Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM }}
        >
          {t(`${KEY}.style.highlightNote`)}
        </Typography.Text>
      )}
    </Flex>
  );

  // Outside the Popover, so the button stays its trigger and keeps its own click.
  // React bubbles clicks out of the portal too, so this stops the whole
  // popover's (padding included) and the button's from reaching the row.
  return (
    <span
      ref={trigger}
      style={{ display: "inline-flex" }}
      onClick={(event) => event.stopPropagation()}
    >
      <Popover
        open={open}
        onOpenChange={onOpenChange}
        // Click: toggles on the button, and closes on a click outside.
        trigger="click"
        placement="bottomRight"
        arrow={false}
        // Once shown: it mounts hidden, and a hidden button can't take focus.
        afterOpenChange={(isOpen) => {
          if (isOpen) focusPanel();
        }}
        content={content}
      >
        {children}
      </Popover>
    </span>
  );
};

export default StandoutStylePopover;
